import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function BusTicketingBanner() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: 'ri-route-line', labelKey: 'bus.f1.label', descKey: 'bus.f1.desc' },
    { icon: 'ri-shield-check-line', labelKey: 'bus.f2.label', descKey: 'bus.f2.desc' },
    { icon: 'ri-ticket-2-line', labelKey: 'bus.f3.label', descKey: 'bus.f3.desc' },
    { icon: 'ri-wallet-3-line', labelKey: 'bus.f4.label', descKey: 'bus.f4.desc' },
  ];

  return (
    <section ref={sectionRef} className="py-16 bg-emerald-800 overflow-hidden relative">
      <div className="absolute -top-16 -right-16 w-64 h-64 bg-emerald-700/40 rounded-full pointer-events-none"></div>
      <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-emerald-900/40 rounded-full pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-10">
          <div
            className={`flex-1 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-md">
                <i className="ri-bus-2-line text-white text-xl"></i>
              </div>
              <span className="text-amber-400 font-semibold text-sm uppercase tracking-widest">
                {t('bus.badge')}
              </span>
            </div>
            <h2 className="text-4xl font-bold text-white font-playfair mb-4 leading-tight">
              {t('bus.title')}<br />
              <span className="text-amber-400">{t('bus.titleHighlight')}</span>
            </h2>
            <p className="text-emerald-100/80 text-base mb-8 leading-relaxed max-w-lg">
              {t('bus.description')}
            </p>
            <a
              href="https://www.easybook.com/la-la/bus/operator"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-4 rounded-md transition-all duration-200 text-base whitespace-nowrap cursor-pointer"
            >
              <i className="ri-bus-2-line"></i>
              {t('bus.bookNow')}
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          <div
            className={`flex-1 grid grid-cols-2 gap-4 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            {features.map((f) => (
              <div
                key={f.labelKey}
                className="bg-emerald-700/50 border border-emerald-600/40 rounded-lg p-5 flex flex-col gap-2 hover:bg-emerald-700/70 transition-colors duration-200"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-md">
                  <i className={`${f.icon} text-amber-400 text-xl`}></i>
                </div>
                <p className="text-white font-semibold text-sm">{t(f.labelKey)}</p>
                <p className="text-emerald-200/70 text-xs">{t(f.descKey)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-emerald-700/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-emerald-300/60 text-xs">
            {t('bus.poweredBy')}
          </p>
          <a
            href="https://www.easybook.com/la-la/bus/operator"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm cursor-pointer"
          >
            <i className="ri-external-link-line"></i>
            easybook.com
          </a>
        </div>
      </div>
    </section>
  );
}
